import React from 'react'
import {
  Furnishing,
  RealEstateCategory,
  RealEstateFeatures,
  SegmentationType,
  TransactionType,
} from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import { tenantShortTermAccommodationRentSchema } from './tenantShortTermAccommodationRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  estateFeatures,
  floorAndTotalFloor,
  heatingTypeField,
  isFurnished,
  maxNumberOfPersons,
  maxRentalTime,
  minRentalTime,
  parkingSpace,
  petsAllowed,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import {
  additionalCostsField,
  depositField,
  livingSpaceField,
  priceField,
  pricePeriodTypeField,
  roomField,
  startRentalDateField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { BuildingTypeOption, CalendaredRentPriceTypeOption } from '../../../../../@types/dropdown-types'
import {
  buildingTypeField,
  BuildingTypeTranslationKey,
} from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { priceToApiShortTermRent } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { getDefaultClasses } from '../../../../../utils/style-utils'

const tenantShortTermAccommodationRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          priceField(PriceFieldType.FLAT_RATE_RENT),
          pricePeriodTypeField(),
          roomField(getDefaultClasses('leftOnAll')),
          startRentalDateField(getDefaultClasses('rightOnAll')),
          livingSpaceField(),
          buildingTypeField(
            'shortTermLiving',
            [BuildingTypeOption.FLAT, BuildingTypeOption.HOUSE, BuildingTypeOption.ROOM, BuildingTypeOption.APARTMENT],
            getDefaultClasses('right'),
            BuildingTypeTranslationKey.SHORT_TERM_ACCOMMODATION
          ),
          additionalCostsField(),
          depositField(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          estateFeatures([
            RealEstateFeatures.BALCONY_TERRACE,
            RealEstateFeatures.GUEST_TOILET,
            RealEstateFeatures.ELEVATOR,
            RealEstateFeatures.SHARED_GARDEN,
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.NON_SMOKER,
          ]),
          isFurnished(),
          petsAllowed(),
          heatingTypeField(),
          maxNumberOfPersons(),
          minRentalTime(),
          maxRentalTime(),
          floorAndTotalFloor('left'),
          parkingSpace({ showParkingSpaceType: true, hasGarageLabel: true }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasLocationNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[MultimediaUploaderType.PICTURE, MultimediaUploaderType.VIDEO]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const tenantShortTermAccommodationRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.tenant',
    estateType: 'wizard.navigation.header.short_term_accommodation',
    path: TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
    name: FlowName.TENANT_SHORT_TERM_ACCOMMODATION_RENT,
    validationSchema: tenantShortTermAccommodationRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.SHORT_TERM_ACCOMMODATION,
      segmentation: SegmentationType.TENANT,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiShortTermRent,
  },
  defaultStateValues: {
    furnishing: Furnishing.UNFURNISHED,
    hideStreetAndStreetNumber: false,
    pricePeriodType: { value: CalendaredRentPriceTypeOption.DAY, label: 'Tag' },
  },
  flowScreens: tenantShortTermAccommodationRentFlowScreens,
}
